// Reusable 5-part reasoning renderer for opportunities & ghost actions.
// Backend field mapping (5-step reasoning_summary):
//   what_i_see_nl | why_this_matters_nl | what_i_advise_nl
//   expected_cost_return_nl (always HYPOTHESE-labelled) | how_confident_nl
//   deep_dive: { evidence_snippets (legacy strings), evidence_items (with
//               provenance: knowledge_item_id, source, source_name,
//               authority_tier, data_layer), assumptions, conflicting_signals }
import { useState } from "react";
import { Link } from "react-router-dom";
import { t } from "@/i18n";
import { Eye, EyeOff, ShieldCheck, ArrowUpRight } from "lucide-react";

const CONF_STYLE = {
  HIGH:   "text-pos-green border-pos-green/40 bg-pos-green/5",
  MEDIUM: "text-warn-amber border-warn-amber/40 bg-warn-amber/5",
  LOW:    "text-text-muted border-border-hairline bg-bg-panel-raised",
};

// Tier badges: outline pills (Fase-1 spec) — border+text carry the tier color.
const TIER_STYLE = {
  TIER_A: "text-pos-green",
  TIER_B: "text-info-blue",
  TIER_C: "text-tier-c-grey",
  TIER_D: "text-text-subtle",
};

const TIER_LABEL = {
  TIER_A: "evidence_tier_a",
  TIER_B: "evidence_tier_b",
  TIER_C: "evidence_tier_c",
  TIER_D: "evidence_tier_d",
};

// Compact evidence-item row: text + tier badge + source badge + BB link.
// Shared visual grammar with `AskResponseCard` in DashboardPage.jsx.
export function EvidenceItem({ item, testId }) {
  if (!item) return null;
  const tier = item.authority_tier;
  const src = item.source_name || item.source;
  const kid = item.knowledge_item_id;
  return (
    <li data-testid={testId} className="border-l-2 border-accent-teal/30 pl-2.5 py-0.5">
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        {tier && TIER_LABEL[tier] && (
          <span
            data-testid={`${testId}-tier`}
            className={`pill-outline ${TIER_STYLE[tier] || TIER_STYLE.TIER_C}`}
          >
            {t(`dashboard.${TIER_LABEL[tier]}`)}
          </span>
        )}
        {src && (
          <span
            data-testid={`${testId}-source`}
            className="pill-filled"
          >
            {src}
          </span>
        )}
        {kid && (
          <Link
            to={`/business-brain#ki-${kid}`}
            data-testid={`${testId}-bb-link`}
            className="text-[10px] text-accent-teal hover:text-accent-teal-dim inline-flex items-center gap-0.5 transition-colors duration-exec"
          >
            {t("dashboard.evidence_view_in_brain")}
            <ArrowUpRight className="w-2.5 h-2.5" />
          </Link>
        )}
      </div>
      <div className="text-xs text-text-primary/85 leading-snug">{item.text}</div>
    </li>
  );
}

export function FivePartReasoning({ reasoning = {}, confidence, scoreBreakdown, compact = false, testidPrefix = "reasoning" }) {
  const [showDeep, setShowDeep] = useState(false);
  const {
    what_i_see_nl, why_this_matters_nl, what_i_advise_nl,
    expected_cost_return_nl, how_confident_nl, deep_dive = {},
  } = reasoning || {};

  const confKey = String(confidence || "").toUpperCase();
  const confBadge = CONF_STYLE[confKey] || CONF_STYLE.LOW;

  const Section = ({ label, body, id, isHypothesis }) => body ? (
    <div className="mb-3" data-testid={`${testidPrefix}-${id}`}>
      <div className="text-[10px] text-neutral-500 font-semibold mb-1 flex items-center gap-2">
        {label}
        {isHypothesis && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
            {t("ledger.hypothesis_label")}
          </span>
        )}
      </div>
      <div className={`text-text-primary/85 leading-relaxed ${compact ? "text-xs" : "text-sm"}`}>
        {body}
      </div>
    </div>
  ) : null;

  const evidenceItems = Array.isArray(deep_dive.evidence_items)
    ? deep_dive.evidence_items : [];
  const legacySnippets = Array.isArray(deep_dive.evidence_snippets)
    ? deep_dive.evidence_snippets : [];

  return (
    <div>
      <Section id="what-i-see" label={t("ledger.step_what_i_see")} body={what_i_see_nl} />
      <Section id="why" label={t("ledger.step_why")} body={why_this_matters_nl} />
      <Section id="advise" label={t("ledger.step_advise")} body={what_i_advise_nl} />
      <Section id="cost-return" label={t("ledger.step_cost_return")}
                body={expected_cost_return_nl} isHypothesis />
      <div className="mb-3" data-testid={`${testidPrefix}-confidence`}>
        <div className="text-[10px] text-neutral-500 font-semibold mb-1">
          {t("ledger.step_confidence")}
        </div>
        <div className="flex items-center gap-2">
          {confKey && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${confBadge}`}>
              {(() => {
                const nl = t(`enum.confidence_pill.${confKey}`);
                return nl && !nl.startsWith("enum.") ? nl : confKey;
              })()}
            </span>
          )}
          <div className={`text-text-primary/85 leading-relaxed ${compact ? "text-xs" : "text-sm"}`}>
            {how_confident_nl || "—"}
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 text-[10px] text-text-muted">
        <ShieldCheck className="w-3 h-3 text-pos-green" />
        {t("ledger.ghost_only_note")}
      </div>

      <button
        type="button"
        onClick={() => setShowDeep((v) => !v)}
        data-testid={`${testidPrefix}-deep-dive-toggle`}
        className="mt-3 text-[11px] text-blue-700 hover:text-blue-900 flex items-center gap-1"
      >
        {showDeep ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
        {showDeep ? t("ledger.hide_deep_dive") : t("ledger.deep_dive")}
      </button>

      {showDeep && (
        <div data-testid={`${testidPrefix}-deep-dive-panel`}
             className="mt-3 rounded-md border border-border-hairline bg-bg-panel-raised p-3 space-y-3 text-xs text-text-primary/85 leading-relaxed">
          {(evidenceItems.length > 0 || legacySnippets.length > 0) && (
            <div>
              <div className="text-[10px] text-neutral-500 font-semibold mb-1">
                {t("ledger.dd_evidence")}
              </div>
              {evidenceItems.length > 0 ? (
                <ul className="space-y-1.5" data-testid={`${testidPrefix}-evidence-items`}>
                  {evidenceItems.map((it, i) => (
                    <EvidenceItem key={i} item={it}
                                   testId={`${testidPrefix}-evidence-item-${i}`} />
                  ))}
                </ul>
              ) : (
                <ul className="space-y-0.5 list-disc pl-4">
                  {legacySnippets.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}
          {Array.isArray(deep_dive.assumptions) && deep_dive.assumptions.length > 0 && (
            <div>
              <div className="text-[10px] text-neutral-500 font-semibold mb-1">
                {t("ledger.dd_assumptions")}
              </div>
              <ul className="space-y-0.5 list-disc pl-4">
                {deep_dive.assumptions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          {/* Conflicting signals — ALWAYS rendered (empty state shown italic
              so the ledger remains transparent rather than hiding the section). */}
          <div data-testid={`${testidPrefix}-conflicting`}>
            <div className="text-[10px] text-neutral-500 font-semibold mb-1">
              {t("ledger.dd_conflicting")}
            </div>
            {Array.isArray(deep_dive.conflicting_signals) && deep_dive.conflicting_signals.length > 0 ? (
              <ul className="space-y-0.5 list-disc pl-4">
                {deep_dive.conflicting_signals.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            ) : (
              <div className="italic text-neutral-500" data-testid={`${testidPrefix}-conflicting-empty`}>
                {t("ledger.dd_conflicting_empty")}
              </div>
            )}
          </div>
          {scoreBreakdown && Object.keys(scoreBreakdown).length > 0 && (
            <div>
              <div className="text-[10px] text-neutral-500 font-semibold mb-1">
                {t("ledger.dd_score_breakdown")}
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(scoreBreakdown).map(([k, v]) => (
                  <div key={k} className="flex items-start gap-2">
                    <span className="text-[9px] text-neutral-500 min-w-[70px]">{k}</span>
                    <span className="font-mono text-[10px] text-neutral-700 tabular-nums">{v?.value ?? "—"}</span>
                    <span className="text-neutral-600">{v?.reason_nl}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
